import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, Clock, Calendar as CalendarIcon } from "lucide-react";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { API, BACKEND_URL } from "@/config";

const resolveImg = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);
// Absolute-URL builder for OG tags (social crawlers need full URLs, not relative paths).
const absoluteUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  if (typeof window !== "undefined") return `${window.location.origin}${u.startsWith("/") ? "" : "/"}${u}`;
  return u;
};

// Markdown → HTML component map. Themed to match the Field Journal aesthetic so
// admin-authored H2/H3, quotes, lists, and inline images look editorial instead
// of default browser styling.
const mdComponents = {
  h1: ({ node, ...p }) => <h1 className="font-serif text-3xl md:text-4xl mt-12 mb-4 text-[#F6F1E4] leading-tight" {...p} />,
  h2: ({ node, ...p }) => <h2 className="font-serif text-2xl md:text-3xl mt-12 mb-4 text-[#F6F1E4] leading-tight" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-serif text-xl md:text-2xl mt-8 mb-3 text-[#FFD166]" {...p} />,
  h4: ({ node, ...p }) => <h4 className="font-serif text-lg mt-6 mb-2 text-[#F6F1E4]" {...p} />,
  p: ({ node, ...p }) => <p className="text-[#E4E9EF] text-lg leading-relaxed font-light mb-5" {...p} />,
  a: ({ node, ...p }) => <a className="text-[#FFD166] underline decoration-[#FFD166]/40 underline-offset-2 hover:decoration-[#FFD166]" target={p.href?.startsWith("http") ? "_blank" : undefined} rel={p.href?.startsWith("http") ? "noreferrer" : undefined} {...p} />,
  strong: ({ node, ...p }) => <strong className="text-[#F6F1E4] font-semibold" {...p} />,
  em: ({ node, ...p }) => <em className="italic text-[#F6F1E4]" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-6 my-5 space-y-2 text-[#E4E9EF] text-lg font-light marker:text-[#FFD166]" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-6 my-5 space-y-2 text-[#E4E9EF] text-lg font-light marker:text-[#FFD166]" {...p} />,
  li: ({ node, ...p }) => <li className="pl-1 leading-relaxed" {...p} />,
  blockquote: ({ node, ...p }) => (
    <blockquote className="my-8 pl-5 border-l-4 border-[#FFD166]/60 italic text-[#F6F1E4]/90 text-xl font-serif" {...p} />
  ),
  code: ({ node, inline, ...p }) =>
    inline
      ? <code className="px-1.5 py-0.5 rounded bg-black/40 border border-[#FFD166]/15 text-[#FFD166] text-[0.9em] font-mono" {...p} />
      : <code className="block p-4 rounded-lg bg-black/50 border border-[#FFD166]/15 text-[#F6F1E4] text-sm font-mono overflow-x-auto" {...p} />,
  hr: () => <hr className="my-10 border-none h-px bg-gradient-to-r from-transparent via-[#FFD166]/30 to-transparent" />,
  img: ({ node, src, alt, ...p }) => (
    <span className="block my-8">
      <img src={resolveImg(src)} alt={alt || ""} className="w-full rounded-2xl border border-[#FFD166]/15" {...p} />
      {alt && <span className="block mt-2 text-center text-xs text-[#B9C8D5] italic">{alt}</span>}
    </span>
  ),
  table: ({ node, ...p }) => (
    <div className="my-6 overflow-x-auto"><table className="w-full text-sm text-left border-collapse" {...p} /></div>
  ),
  th: ({ node, ...p }) => <th className="border-b border-[#FFD166]/30 px-3 py-2 font-serif text-[#FFD166]" {...p} />,
  td: ({ node, ...p }) => <td className="border-b border-white/10 px-3 py-2 text-[#E4E9EF]" {...p} />,
};

// A quiet, editorial article layout that matches the site's Field Journal aesthetic.
// The optional "Featured Rental" block converts the article into a soft sales moment
// so a reader ready to book doesn't have to hunt through the catalog.
export const BlogArticle = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  // The static index.html carries site-wide SEO tags for the home page. On
  // article pages we want the article's own meta to win, so strip the
  // duplicates once on mount and let react-helmet-async manage from here.
  // Runs on the client only; SSR-safe because it's guarded inside useEffect.
  useEffect(() => {
    const selectors = [
      'meta[name="description"]:not([data-rh])',
      'meta[property="og:title"]:not([data-rh])',
      'meta[property="og:description"]:not([data-rh])',
      'meta[property="og:image"]:not([data-rh])',
      'meta[property="og:type"]:not([data-rh])',
      'meta[property="og:url"]:not([data-rh])',
      'meta[name="twitter:title"]:not([data-rh])',
      'meta[name="twitter:description"]:not([data-rh])',
      'meta[name="twitter:image"]:not([data-rh])',
      'meta[name="twitter:card"]:not([data-rh])',
    ];
    const removed = [];
    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        removed.push({ tag: el.tagName, attrs: [...el.attributes].map((a) => [a.name, a.value]) });
        el.parentNode?.removeChild(el);
      });
    });
    // Put the site-wide defaults back when the user navigates away from the
    // article so Home / Book / etc. still ship correct social tags.
    return () => {
      removed.forEach(({ tag, attrs }) => {
        const el = document.createElement(tag.toLowerCase());
        attrs.forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      });
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPost(null);
    setError(null);
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => { if (!cancelled) setPost(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.status === 404 ? "not_found" : "error"); });
    return () => { cancelled = true; };
  }, [slug]);

  if (error === "not_found") {
    return (
      <div className="min-h-screen text-[#F6F1E4]" style={{ background: "linear-gradient(180deg,#040A0F,#0A2438 50%,#040A0F)" }}>
        <Nav />
        <div className="container pb-24 text-center" style={{ paddingTop: "12rem" }}>
          <p className="eyebrow text-[#FFD166]">The Field Journal</p>
          <h1 className="font-serif text-4xl md:text-5xl mt-4">This story has drifted off downstream.</h1>
          <p className="text-[#B9C8D5] mt-3">The article you're looking for isn't here (or hasn't been published yet).</p>
          <Link to="/#blog" className="inline-flex items-center gap-2 text-[#FFD166] hover:underline mt-6" data-testid="blog-article-back-empty">
            <ArrowLeft className="w-4 h-4" /> Back to the journal
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen text-[#F6F1E4]" style={{ background: "linear-gradient(180deg,#040A0F,#0A2438 50%,#040A0F)" }}>
        <Nav />
        <div className="container pb-24 text-center text-[#B9C8D5]" style={{ paddingTop: "12rem" }}>Loading…</div>
      </div>
    );
  }

  const featured = post.featured_product;
  const seoTitle = (post.seo_title || `${post.title} · Enchanted Gear Rentals`).slice(0, 200);
  const seoDescription = (post.seo_description || post.excerpt || "").slice(0, 300);
  const seoImage = absoluteUrl(post.image);
  const pageUrl = typeof window !== "undefined" ? `${window.location.origin}/journal/${post.slug || slug}` : "";

  return (
    <div className="min-h-screen text-[#F6F1E4]" style={{ background: "linear-gradient(180deg,#040A0F,#0A2438 50%,#040A0F)" }}>
      <Helmet>
        <title>{seoTitle}</title>
        {seoDescription && <meta name="description" content={seoDescription} />}
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seoTitle} />
        {seoDescription && <meta property="og:description" content={seoDescription} />}
        {seoImage && <meta property="og:image" content={seoImage} />}
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="Enchanted Gear Rentals" />
        {post.date && <meta property="article:published_time" content={new Date(post.date).toISOString()} />}
        <meta name="twitter:card" content={seoImage ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={seoTitle} />
        {seoDescription && <meta name="twitter:description" content={seoDescription} />}
        {seoImage && <meta name="twitter:image" content={seoImage} />}
      </Helmet>
      <Nav />

      <article className="pb-20" style={{ paddingTop: "9rem" }}>
        <div className="container max-w-3xl">
          <Link to="/#blog" className="inline-flex items-center gap-2 text-xs text-[#B9C8D5] hover:text-[#FFD166]" data-testid="blog-article-back">
            <ArrowLeft className="w-3.5 h-3.5" /> The Field Journal
          </Link>

          <p className="eyebrow mt-8 text-[#FFD166]">Field Journal</p>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight mt-3" data-testid="blog-article-title">{post.title}</h1>

          <div className="mt-4 flex items-center gap-5 text-xs text-[#B9C8D5]">
            {post.date && (
              <span className="inline-flex items-center gap-1.5"><CalendarIcon className="w-3.5 h-3.5" /> {format(new Date(post.date), "MMM d, yyyy")}</span>
            )}
            {post.read_time && (
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {post.read_time}</span>
            )}
          </div>

          {post.image && (
            <div className="mt-8 rounded-2xl overflow-hidden border border-[#FFD166]/15">
              <img src={resolveImg(post.image)} alt={post.title} className="w-full aspect-[16/9] object-cover" />
            </div>
          )}

          {/* Body — Markdown with GFM extensions. Admins can drop in headings, bold,
              italic, lists, block quotes, links, and inline images and they render
              like a proper magazine feature instead of a flat paragraph blob. */}
          <div className="blog-article-body mt-10" data-testid="blog-article-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {post.body || post.excerpt || ""}
            </ReactMarkdown>
          </div>

          {featured && (
            <FeaturedRentalCTA product={featured} onBook={() => nav("/#catalog")} />
          )}

          <div className="mt-14 pt-10 border-t border-[#FFD166]/15 flex items-center justify-between flex-wrap gap-4">
            <Link to="/#blog" className="text-sm text-[#B9C8D5] hover:text-[#FFD166] inline-flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> More from the journal
            </Link>
            <Link to="/#trip-setup" className="btn-primary inline-flex items-center gap-2" data-testid="blog-article-book-now">
              Book your trip <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

const FeaturedRentalCTA = ({ product, onBook }) => (
  <aside
    className="mt-12 rounded-2xl border border-[#FFD166]/30 bg-gradient-to-br from-[#0F1E2C]/95 to-[#040A0F]/95 backdrop-blur p-6 md:p-7 shadow-[0_10px_40px_rgba(0,0,0,0.4)]"
    data-testid="blog-featured-rental"
  >
    <p className="eyebrow text-[#F4A93A]">Recommended in this story</p>
    <div className="mt-4 grid md:grid-cols-[140px_1fr_auto] gap-5 items-center">
      {product.image ? (
        <img src={resolveImg(product.image)} alt={product.name} className="w-full md:w-32 aspect-square object-cover rounded-xl border border-[#FFD166]/20" />
      ) : (
        <div className="w-full md:w-32 aspect-square rounded-xl bg-black/40 border border-[#FFD166]/20" />
      )}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFD166]">{product.category}</div>
        <div className="font-serif text-2xl md:text-3xl mt-1" data-testid="blog-featured-name">{product.name}</div>
        {product.tagline && <div className="text-sm italic text-[#B9C8D5] mt-1">{product.tagline}</div>}
        {typeof product.tier_1_3 === "number" && (
          <div className="mt-3 text-xs text-[#B9C8D5]">
            From <span className="text-[#FFD166] font-serif text-lg">${Number(product.tier_1_3).toFixed(2)}</span> per day
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onBook}
        className="btn-primary whitespace-nowrap inline-flex items-center gap-2"
        data-testid="blog-featured-book-btn"
      >
        Book This Item <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </aside>
);
